/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, HostBinding, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommunicationType, ContactListItemDto } from '@gh/builder-shared-data';
import {
  createDialogRoute,
  FormState,
  FormStateBuilder,
  getDialogInitData,
  TextTypes,
  whenCreateOrEditMode,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import {
  DialogInput,
  DialogService,
  FormMode,
  getControlValue,
  NotificationService,
  responsiveDialogOverlayPaneClass,
  TinymceSettings,
  tmSettingsBuilder,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
  AutocompleteLoadMoreEvent,
  setControlValue,
} from '@gh/core-ui';
import { filter$, map$ } from '@gh/rx/operators';

import { isEmpty, remove } from 'lodash';
import { BuilderUserContext } from '../../../user';

import {
  CommunicationsBusinessService,
  EmailDialogConfig,
  isCreateEmailDialogConfig,
} from '../../resources/communications-business.service';
import {
  CommunicationListItem,
  EmailDetails,
  emailDetailsFromCommunicationItem,
  emailDetailsToCommunicationItem,
} from '../../resources/communications.model';
import { ChangeDetectorRef } from '@angular/core';

@Component({
  standalone: false,
  selector: 'gh-email-dialog',
  templateUrl: 'email-dialog.component.html',
  styleUrls: ['email-dialog.component.scss'],
})

export class EmailDialogComponent implements OnInit, DialogInput<EmailDialogConfig> {
  input: EmailDialogConfig;
  form: FormState<EmailDetails>;
  emailTemplates: any[];
  tmResponsiveSettings: TinymceSettings;
  tmSettings: TinymceSettings;
  editor: any;

  private preventAutocompleteOpen = false;

  private getSelectedEmails(): string[] {
    const emailList = getControlValue(this.form.group, 'emailList') || [];
    const emailCcList = getControlValue(this.form.group, 'emailListCc') || [];
    const emailBccList = getControlValue(this.form.group, 'emailListBcc') || [];
    return [...emailList, ...emailCcList, ...emailBccList];
  }

  private filterAlreadySelectedContacts(contacts: ContactListItemDto[], selectedEmails?: string[]): ContactListItemDto[] {
    const emails = selectedEmails || this.getSelectedEmails();

    return contacts.filter(contact => {
      if (!contact.email) {
        return true;
      }

      return !emails.includes(contact.email);
    });
  }

  constructor(private formStateBuilder: FormStateBuilder,
    private userContext: BuilderUserContext,
    private businessService: CommunicationsBusinessService,
    private waitingService: WaitingService,
    public dialogRef: MatDialogRef<EmailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private dialogData: any,
    private notificationService: NotificationService,
    private cdRef: ChangeDetectorRef,
    private dialogService: DialogService) {

  }

  @HostBinding('class.m-has-attachments')
  get hasAttachments(): boolean {
    return this.form.createMode && !isEmpty(getControlValue(this.form.group, 'files'));
  }

  get gFolderId(): Maybe<ID> {
    return this.input.gFolderId;
  }

  ngOnInit(): void {
    const { email } = getDialogInitData(this.dialogData);
    const emails = email.email ? email.email.split(/[,;]+/).filter((e: string) => e && e.trim()) : [];
    const otherEmail = emails[1] || '';
    const primaryEmail = emails[0] || '';
    const emailArray: any[] = [];
    if (emails.length > 2) {
      emailArray.push(...emails.slice(2));
    }
    if (primaryEmail) {
      emailArray.push(primaryEmail);
    }
    if (otherEmail && otherEmail !== primaryEmail) {
      emailArray.push(otherEmail);
    }
    this.form = this.formStateBuilder.buildPlain({
      value: {
        ...emailDetailsFromCommunicationItem(email),
        email: this.userContext.sandbox ? void 0 : primaryEmail,
        contactEmailList: '',
        emailList: this.userContext.sandbox ? [] : emailArray,
        otherEmail: otherEmail,
        primaryEmail: primaryEmail,
        emailBcc: '',
        emailCc: '',
        emailListCc: [],
        emailListBcc: [],
      },
      mode: isCreateEmailDialogConfig(email) &&
        (!this.input.mode || this.input.mode === FormMode.EditMode) ? FormMode.CreateMode : FormMode.ViewMode,
      enabled: whenCreateOrEditMode(),
      children: {
        email: { type: TextTypes.EmailList },
        emailCc: { enabled: true, type: TextTypes.EmailList },
        emailBcc: { enabled: true, type: TextTypes.EmailList },
        subject: { required: true },
        message: { required: true },
        contactEmailList: { enabled: true },
        otherEmail: { enabled: true },
        primaryEmail: { enabled: true },
        emailList: { required: true, enabled: true },
        emailListCc: { enabled: true },
        emailListBcc: { enabled: true },
        template: void 0,
        files: void 0,
        contactId: void 0,
        jobId: void 0,
      },
    });
    if (this.form.viewMode) {
      this.form.get('contactEmailList')?.disable();
      this.form.get('emailCc')?.disable();
      this.form.get('emailBcc')?.disable();
    }
    const events = {
      onChange: (event: any) => {
        this.onMessageChange(event);
      },
      onPaste: (event: any) => {
        this.onMessageChange(event);
      },
      onInit: (editor: any) => {
        this.editor = editor;
      },
    };
    const builder = tmSettingsBuilder()
      .pasteOptions({ asText: false })
      .scayt()
      .styles('body { overflow-wrap: break-word; }')
      .height(270)
      .statusBar(false)
      .build();
    const builderResponsive = tmSettingsBuilder()
      .pasteOptions({ asText: false })
      .scayt()
      .styles('body { overflow-wrap: break-word; }')
      .height(270)
      .statusBar(false)
      .build();
    this.tmSettings = {
      ...builder,
      ...events,
    };
    this.tmResponsiveSettings = {
      ...builderResponsive,
      ...events,
    };
    this.preventAutocompleteOpen = true;
    this.loadEmailTemplates();
  }

  onCancel(): void {
    if (this.form.group.dirty || this.form.createMode) {
      this.dialogService
        .confirmation(yesNoConfirmation('msg_changes_not_saved')).pipe(
          filter$(yesAnswer))
        .subscribe(() => this.dialogRef.close());
    } else {
      this.dialogRef.close();
    }
  }

  loadEmailTemplates(): void {
    this.businessService.getEmailTemplates().subscribe((list) => this.emailTemplates = list, this.notificationService.processAll);
  }

  loadContacts(event: AutocompleteLoadMoreEvent, controllerName: string): void {
    const selectedEmails = this.getSelectedEmails();
    event.load(
      this.businessService.getContacts(event.query, controllerName).pipe(
        map$((res: ContactListItemDto[]) => {
          if (res.length === 0) {
            const inputValue = event.query?.filter || '';
            const control = this.form.get(`${controllerName}`);
            if (control && !this.isValidEmail(inputValue)) {
              control.markAsTouched();
              control.setErrors({ invalidEmail: true });
            } else {
              this.form.get(`${controllerName}`)?.setErrors(null);
            }
          } else {
            this.form.get(`${controllerName}`)?.setErrors(null);
          }
          return this.filterAlreadySelectedContacts(res, selectedEmails);
        })
      )
    );
  }

  addEmailFromInput(controlName: 'emailList' | 'emailListCc' | 'emailListBcc', inputControlName: 'contactEmailList' | 'emailCc' | 'emailBcc', event?: FocusEvent | KeyboardEvent): void {
    const inputValue = event?.target && 'value' in event.target ? (event.target as HTMLInputElement).value : this.form.get(inputControlName)?.value;
    const value = (typeof inputValue === 'string' ? inputValue : '').trim();
    if (!value) {
      return;
    }
    this.addEmailInput({ value }, controlName, inputControlName, true);
    this.form.get(inputControlName)?.setValue('', { emitEvent: false });
    if (event?.target && 'value' in event.target) {
      (event.target as HTMLInputElement).value = '';
    }
  }

  addEmailInput(event: any, controlName: 'emailList' | 'emailListCc' | 'emailListBcc', inputControlName: 'contactEmailList' | 'emailCc' | 'emailBcc', isFromInput: boolean = false): void {
    const selectedEmails = this.getSelectedEmails();
    if (!event) {
      return;
    }
    let value: string | null = null;
    if (isFromInput && event?.value) {
      value = (event.value || '').trim();
    } else if (!isFromInput && event?.email) {
      value = event.email.trim();
    }
    if (!value) {
      return;
    }

    const currentList: string[] = this.form.get(controlName)?.value || [];

    if (this.isValidEmail(value)) {
      if (!selectedEmails.includes(value)) {
        this.form.get(controlName)?.setValue([...currentList, value]);
        this.form.get(inputControlName)?.setValue('', { emitEvent: false });
        this.form.get(controlName)?.setErrors(null);
        this.cdRef.detectChanges();
      } else {
        this.form.get(controlName)?.setErrors({ duplicate: true });
      }
    } else if (isFromInput && !this.isValidEmail(value)) {
      this.form.get(controlName)?.setErrors({ invalidEmail: true });
    }
  }

  addEmailGeneric(event: any, controlName: 'emailList' | 'emailListCc' | 'emailListBcc', inputControlName: 'contactEmailList' | 'emailCc' | 'emailBcc', isFromInput: boolean = false): void {
    const selectedEmails = this.getSelectedEmails();
    if (!event) {
      return;
    }
    let value: string | null = null;
    if (isFromInput && event?.value) {
      value = (event.value || '').trim();
    } else if (!isFromInput && event?.email) {
      value = event.email.trim();
    }
    if (!value) {
      return;
    }
    const currentList: string[] = this.form.get(controlName)?.value || [];
    if (this.isValidEmail(value)) {
      if (!selectedEmails.includes(value)) {
        this.form.get(controlName)?.setValue([...currentList, value]);
        this.form.get(inputControlName)?.setValue('', { emitEvent: false });
        this.form.get(controlName)?.setErrors(null);
        this.cdRef.detectChanges();
      }
    } else if (isFromInput && !this.isValidEmail(value)) {
      this.form.get(controlName)?.setErrors({ invalidEmail: true });
    }
  }

  removeEmail(email: string): void {
    const emails = this.form.get('emailList')?.value || [];
    const index = emails.indexOf(email);
    if (index >= 0) {
      emails.splice(index, 1);
      this.form.get('emailList')?.setValue(emails);
    }
    if (emails.length === 0) {
      this.preventAutocompleteOpen = true;
    }
    this.cdRef.detectChanges();
  }

  onRemoveContactToCc(selectedEmail: string): void {
    const emailCcList = getControlValue(this.form.group, 'emailListCc');
    remove(emailCcList, (email: string) => email === selectedEmail);
    setControlValue(this.form.group, 'emailListCc', emailCcList);

    this.preventAutocompleteOpen = true;
    setTimeout(() => {
      this.preventAutocompleteOpen = false;
    }, 200);
  }

  onRemoveContactToBcc(selectedEmail: string): void {
    const emailBccList = getControlValue(this.form.group, 'emailListBcc');
    remove(emailBccList, (email: string) => email === selectedEmail);
    setControlValue(this.form.group, 'emailListBcc', emailBccList);

    this.preventAutocompleteOpen = true;
    setTimeout(() => {
      this.preventAutocompleteOpen = false;
    }, 200);
  }

  onSelectTemplate({ value }: any): void {
    const template = this.emailTemplates.find((item) => item.id === value);
    this.businessService.getFilledEmailTemplate(template.id, this.input.contactId, this.input.jobId).subscribe((res) => {
      this.form.group.patchValue({
        email: this.form.value.email,
        emailCc: '',
        emailBcc: '',
        contactEmailList: '',
        emailList: this.form.value.emailList,
        emailListCc: this.form.value.emailListCc,
        emailListBcc: this.form.value.emailListBcc,
        subject: res.subject,
        message: res.body,
        template: this.form.value.template,
        files: this.form.value.files,
        contactId: this.form.value.contactId,
        jobId: this.form.value.jobId,
      });
      if (res.unresolvedPlaceholders.length) {
        const unresolved = res.unresolvedPlaceholders.join(', ');
        this.notificationService.warning(`There are no values for ${unresolved}`);
      }
    });
  }

  isValidEmail(email: string): boolean {
    const emailRegex = /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/;
    return emailRegex.test(email.trim());
  }

  onSend(): void {
    const getEmails = (controlName: string): string =>
      (getControlValue(this.form.group, controlName) || []).join(',');
    const emailList = getEmails('emailList');
    this.form.markAllAsTouched();
    if (this.form.group.invalid || emailList.length === 0) {
      this.form.markAllAsTouched();
      if (emailList.length === 0) {
        this.form.get('emailList')?.setErrors({ required: true });
      }
      return;
    }
    this.businessService.sendEmail(this.input.entryPoint, this.input, {
      ...emailDetailsToCommunicationItem({
        communicationType: CommunicationType.Email,
        email: getEmails('emailList'),
        emailCc: getEmails('emailListCc'),
        emailBcc: getEmails('emailListBcc'),
        subject: this.form.value.subject,
        message: this.form.value.message,
        files: this.form.value.files,
        contactId: this.input.contactId,
        jobId: this.input.jobId,
      }),
      communicationType: CommunicationType.Email,
      contactTypeCode: this.input.email!.contactTypeCode,
    }).pipe(
      this.waitingService.operator())
      .subscribe(
        (communicationListItem: CommunicationListItem) => {
          this.dialogRef.close(communicationListItem);
        },
        this.notificationService.processAll);
  }
  onMessageChange(event: any): void {
    const text = this.editor.getContent({ format: 'text' }).trim();
    if (!text) {
      this.form.getControl('message').setErrors({ required: true });
    }
  }
}

export const emailDialogRoute = createDialogRoute({
  component: EmailDialogComponent,
  init: {
    service: CommunicationsBusinessService,
    selector: 'initEmailDialogView',
  },
  config: {
    disableClose: true,
    maxHeight: '85vh',
    panelClass: responsiveDialogOverlayPaneClass(),
  },
});
